import { Geometry } from "@turf/helpers";
import { PropsWithChildren } from "react";
import { LayerProps, Map } from "react-map-gl";
import {
  DefaultControls,
  DynamicLegend,
  MapWrapper,
  UserLocationPin,
  useStaticMap,
  UseStaticMapOptions,
} from "../common";
import DistancePopup from "../hazard-map/src/distance-popup";
import { GeoJsonLayer } from "./layers";

export interface StaticMapProps extends PropsWithChildren<UseStaticMapOptions> {
  mapboxAccessToken?: string;
}
function getLayerStyle(geometry?: Geometry, color?: string): LayerProps {
  if (!geometry) {
    return {
      type: "circle",
      paint: {
        "circle-radius": 10,
        "circle-color": color ?? "#007cbf",
      },
    };
  }
  switch (geometry.type) {
    case "Point":
      return {
        type: "circle",
        paint: {
          "circle-radius": 10,
          "circle-color": color ?? "#007cbf",
        },
      };
    case "LineString":
      return {
        type: "line",
        paint: {
          "line-color": color ?? "#007cbf",
          "line-width": 5,
        },
      };
    case "MultiLineString":
      return {
        type: "line",
        paint: {
          "line-color": color ?? "#007cbf",
          "line-width": 5,
        },
      };
    case "Polygon":
      return {
        type: "fill",
        paint: {
          "fill-color": color ?? "#007cbf",
          "fill-opacity": 0.25,
        },
      };
    case "MultiPolygon":
      return {
        type: "fill",
        paint: {
          "fill-color": color ?? "#007cbf",
          "fill-opacity": 0.25,
        },
      };

    default:
      return {
        type: "circle",
        paint: {
          "circle-radius": 10,
          "circle-color": color ?? "#007cbf",
        },
      };
  }
}

export const StaticMap = ({
  children,
  mapboxAccessToken,
  ...props
}: StaticMapProps) => {
  const {
    ref,
    onLoad,
    initialViewState,
    resetViewState,
    onMouseEvent,
    hovered,
    legendRows,
    colors,
  } = useStaticMap(props);

  return (
    <MapWrapper
      {...{ w: "100%", h: "100%" }}
      sx={{
        ".mapboxgl-popup-close-button": {
          display: "none",
        },
      }}
    >
      <Map
        ref={ref}
        onLoad={onLoad}
        onMouseMove={onMouseEvent}
        initialViewState={initialViewState}
        mapboxAccessToken={
          mapboxAccessToken ||
          process.env.NEXT_PUBLIC_CLIENT_MAPBOX_ACCESS_TOKEN
        }
        attributionControl={false}
        // disable pitch
        maxPitch={0}
        // disable rotation
        dragRotate={false}
        // disable scroll zooming
        scrollZoom={false}
        mapStyle={
          "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
        }
        style={{ height: "100%", width: "100%" }}
        interactiveLayerIds={props?.featureLayers?.map(
          (layer) => layer.category,
        )}
      >
        {props.featureLayers?.map((layer, idx) => (
          <GeoJsonLayer
            key={layer.category}
            source={{
              id: layer.category,
              data: { type: "FeatureCollection", features: layer?.features! },
              type: "geojson",
            }}
            layer={{
              ...getLayerStyle(
                layer?.features![0]?.geometry as Geometry,
                colors[idx],
              ),
              id: layer.category,
            }}
          />
        ))}
        {children}
        <DefaultControls resetViewport={resetViewState} />
        {legendRows && (
          <DynamicLegend
            legendRows={legendRows}
            position="absolute"
            bottom="1"
            left="1"
            inverted
          />
        )}

        <UserLocationPin userLocation={props.userLocation} />
        {hovered && <DistancePopup {...hovered} />}
      </Map>
    </MapWrapper>
  );
};
